/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react'
import {
  Query,
  QuerySnapshot,
  DocumentReference,
  DocumentSnapshot,
  onSnapshot,
  DocumentData,
} from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { dataCache } from '../cache'

// Authentication aware hook for collections
export function useFirestoreCollectionCache<T extends Record<string, any>>(
  query: Query<DocumentData> | null,
  cacheKey: string,
  transform: (snapshot: QuerySnapshot<DocumentData>) => T[],
  enabled: boolean = true,
  requireAuth: boolean = true // New parameter
) {
  const [data, setData] = useState<T[]>([])
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<Error | null>(null)
  const auth = getAuth()

  useEffect(() => {
    // Don't start subscription if not enabled or no query
    if (!enabled || !query) {
      setLoading(false)
      setData([])
      setError(null)
      return
    }

    // Don't start subscription if auth is not ready
    if (requireAuth && !auth.currentUser) {
      setLoading(true)
      setData([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    // Check cache first
    const cached = dataCache.get(cacheKey)
    if (cached) {
      setData(cached as T[]) // Type assertion here since we know the cache structure
      setLoading(false)
    }

    // Start subscription
    const unsubscribe = onSnapshot(
      query,
      (snapshot: QuerySnapshot<DocumentData>) => {
        try {
          const transformedData = transform(snapshot)
          setData(transformedData)
          dataCache.set(cacheKey, transformedData)
          setError(null)
        } catch (err) {
          console.error('Transform error:', err)
          setError(err as Error)
        }
        setLoading(false)
      },
      (err: Error) => {
        // Only set error if it's not a permissions error during auth
        if (
          !requireAuth ||
          auth.currentUser ||
          err.message !== 'Missing or insufficient permissions.'
        ) {
          console.error('Firestore query error:', err)
          setError(err)
        }
        setLoading(false)
      }
    )

    return () => {
      unsubscribe()
    }
  }, [query, cacheKey, enabled, transform, auth.currentUser, requireAuth])

  return { data, loading, error }
}

// Authentication aware hook for single documents
export function useFirestoreDocCache<T extends Record<string, any>>(
  docRef: DocumentReference<DocumentData> | null,
  cacheKey: string,
  transform: (snapshot: DocumentSnapshot<DocumentData>) => T | null,
  enabled: boolean = true,
  requireAuth: boolean = true // New parameter
) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<Error | null>(null)
  const auth = getAuth()

  useEffect(() => {
    if (!enabled || !docRef) {
      setLoading(false)
      setData(null)
      setError(null)
      return
    }

    // Don't start subscription if auth is not ready
    if (requireAuth && !auth.currentUser) {
      setLoading(true)
      setData(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    // Check cache first
    const cached = dataCache.get(cacheKey)
    if (cached) {
      setData(cached as T) // Type assertion here since we know the cache structure
      setLoading(false)
    }

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot: DocumentSnapshot<DocumentData>) => {
        try {
          const transformedData = transform(snapshot)
          setData(transformedData)
          if (transformedData) {
            dataCache.set(cacheKey, transformedData)
          }
          setError(null)
        } catch (err) {
          console.error('Transform error:', err)
          setError(err as Error)
        }
        setLoading(false)
      },
      (err: Error) => {
        // Only set error if it's not a permissions error during auth
        if (
          !requireAuth ||
          auth.currentUser ||
          err.message !== 'Missing or insufficient permissions.'
        ) {
          console.error('Firestore document error:', err)
          setError(err)
        }
        setLoading(false)
      }
    )

    return () => {
      unsubscribe()
    }
  }, [docRef, cacheKey, enabled, transform, auth.currentUser, requireAuth])

  return { data, loading, error }
}

// Typed version for map cache
export function useFirestoreMapCache<T extends Record<string, any>>(
  query: Query<DocumentData> | null,
  cacheKey: string,
  transform: (snapshot: QuerySnapshot<DocumentData>) => { [key: string]: T },
  enabled: boolean = true,
  requireAuth: boolean = true // New parameter
) {
  const [data, setData] = useState<{ [key: string]: T }>({})
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<Error | null>(null)
  const auth = getAuth()

  useEffect(() => {
    if (!enabled || !query) {
      setLoading(false)
      setData({})
      setError(null)
      return
    }

    // Don't start subscription if auth is not ready
    if (requireAuth && !auth.currentUser) {
      setLoading(true)
      setData({})
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    // Check cache first
    const cached = dataCache.get(cacheKey)
    if (cached) {
      setData(cached as { [key: string]: T }) // Type assertion here since we know the cache structure
      setLoading(false)
    }

    const unsubscribe = onSnapshot(
      query,
      (snapshot: QuerySnapshot<DocumentData>) => {
        try {
          const transformedData = transform(snapshot)
          setData(transformedData)
          dataCache.set(cacheKey, transformedData)
          setError(null)
        } catch (err) {
          console.error('Transform error:', err)
          setError(err as Error)
        }
        setLoading(false)
      },
      (err: Error) => {
        // Only set error if it's not a permissions error during auth
        if (
          !requireAuth ||
          auth.currentUser ||
          err.message !== 'Missing or insufficient permissions.'
        ) {
          console.error('Firestore query error:', err)
          setError(err)
        }
        setLoading(false)
      }
    )

    return () => {
      unsubscribe()
    }
  }, [query, cacheKey, enabled, transform, auth.currentUser, requireAuth])

  return { data, loading, error }
}
